import axios from 'axios';
import axiosInstance, { getStoredToken } from '@core/api/axios-config';
import {
    SubCategoryDto,
    CreateSubCategoryDto,
    UpdateSubCategoryDto,
    SubCategoryPagedResult,
    SubCategoryFilterParams
} from '../types/subcategory.types';

/**
 * SubCategories API Service
 * Replicated from Angular SubCategoryService
 */
export const subcategoriesApi = {
    /**
     * Get all subcategories (unpaginated)
     */
    getAll: async (categoryId?: string): Promise<SubCategoryDto[]> => {
        const params = categoryId ? { categoryId } : {};
        const response = await axiosInstance.get<SubCategoryDto[]>('/subcategories', { params });
        return response.data;
    },

    /**
     * Get paginated subcategories with search and category filtering
     */
    getPaged: async (params: SubCategoryFilterParams): Promise<SubCategoryPagedResult> => {
        const queryParams = new URLSearchParams();
        queryParams.set('page', params.page.toString());
        queryParams.set('pageSize', params.pageSize.toString());

        if (params.search) {
            queryParams.set('search', params.search);
        }
        if (params.categoryId) {
            queryParams.set('categoryId', params.categoryId);
        }

        const response = await axiosInstance.get<SubCategoryPagedResult>('/subcategories/paged', {
            params: queryParams
        });
        return response.data;
    },

    /**
     * Get subcategory by ID
     */
    getById: async (id: string): Promise<SubCategoryDto> => {
        const response = await axiosInstance.get<SubCategoryDto>(`/subcategories/${id}`);
        return response.data;
    },

    /**
     * Create new subcategory
     */
    create: async (data: CreateSubCategoryDto): Promise<SubCategoryDto> => {
        const response = await axiosInstance.post<SubCategoryDto>('/subcategories', data);
        return response.data;
    },

    /**
     * Update existing subcategory
     */
    update: async (id: string, data: UpdateSubCategoryDto): Promise<SubCategoryDto> => {
        const response = await axiosInstance.put<SubCategoryDto>(`/subcategories/${id}`, data);
        return response.data;
    },

    /**
     * Delete subcategory
     */
    delete: async (id: string): Promise<void> => {
        await axiosInstance.delete(`/subcategories/${id}`);
    },

    /**
     * Upload subcategory image
     * Uses robust upload logic bypassing default JSON instance headers
     */
    uploadImage: async (file: File): Promise<{ imageUrl: string }> => {
        const formData = new FormData();
        formData.append('file', file);

        const token = getStoredToken();
        const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

        const response = await axios.post<{ imageUrl: string }>(`${baseURL}/fileupload/subcategory`, formData, {
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
        });
        return response.data;
    }
};
